import Navbar from "../assets/Navbar";
import CardDesign from "../components/cardDesign";
import FooterSection from "assets/Footer";
import { useParams } from "react-router-dom";
import TrendingProducts from "components/dashboardComponents/trendingProducts";
import useGetSubCataloguesWithAllItem from "hook/useGetSubCatalougesWithAllItem";
import { TbPackageOff } from "react-icons/tb";
import { useState, useEffect } from "react";
import ScrollToTopButton from "components/ScrollToTopButton";
import BannerShimmer from "components/shimmer/BannerShimmer";
import ShimmerCard from "components/shimmer/ShimmerCard";
import RevealOnScroll from "components/RevealOnScroll";
import { FaSearch, FaSortAmountDown } from "react-icons/fa";
import NoDataFound from "components/NoDataFound";

const SubCategoryWiseData = () => {
  const { subCatalogueId } = useParams();
  const { subGroupedAllData, loading, error } =
    useGetSubCataloguesWithAllItem(subCatalogueId);

  const [searchTerm, setSearchTerm] = useState("");
  const [sortBy, setSortBy] = useState("featured");
  const [freeDeliveryOnly, setFreeDeliveryOnly] = useState(false);
  const [showScrollTop, setShowScrollTop] = useState(false);

  // Scroll-to-top button
  useEffect(() => {
    const handleScroll = () => setShowScrollTop(window.scrollY > 200);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Reset filters on subCatalogueId change
  useEffect(() => {
    setSearchTerm("");
    setSortBy("featured");
    setFreeDeliveryOnly(false);
  }, [subCatalogueId]);

  const processedItems = subGroupedAllData
    .filter((item) => {
      if (!searchTerm) return true;
      const term = searchTerm.toLowerCase();
      return (
        item.mainTitle?.toLowerCase().includes(term) ||
        item.subCatalogue_title?.toLowerCase().includes(term)
      );
    })
    .filter((item) => {
      if (!freeDeliveryOnly) return true;
      return item.variants?.some((v) => v.freeVariantDelivery === 1);
    })
    .sort((a, b) => {
      const priceA = a.variants?.[0]?.price || 0;
      const priceB = b.variants?.[0]?.price || 0;

      switch (sortBy) {
        case "price_low":
          return priceA - priceB;
        case "price_high":
          return priceB - priceA;
        case "discount_high":
          return (
            (b.variants?.[0]?.discount || 0) - (a.variants?.[0]?.discount || 0)
          );
        case "name_az":
          return a.mainTitle.localeCompare(b.mainTitle);
        default:
          return 0;
      }
    });

  return (
    <div className="bg-gray-100 flex flex-col min-h-[100dvh]">
      <Navbar />

      <div className="container mx-auto relative">
        {/* 🔄 Loading */}
        {loading && (
          <div className="space-y-6 p-4">
            <BannerShimmer />
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
              {Array.from({ length: 8 }).map((_, i) => (
                <ShimmerCard key={i} />
              ))}
            </div>
          </div>
        )}

        {/* 🚫 No Data */}
        {!loading && !error && subGroupedAllData.length === 0 && (
          <div className="text-center text-[#265fc9] py-10 my-5">
            <TbPackageOff className="mx-auto w-40 h-40 mb-5 text-backgroundColor animate-pulse" />
            <h3 className="text-lg font-semibold mt-5">
              শীঘ্রই নতুন পণ্য যুক্ত করা হবে।
            </h3>
          </div>
        )}

        {/* ✅ Data */}
        {!loading && !error && subGroupedAllData.length > 0 && (
          <>
            {/* Banner */}
            <RevealOnScroll>
              <section className="relative overflow-hidden shadow-lg mb-4">
                <img
                  src={`https://backend.nuhababy.com/${subGroupedAllData[0].banner_image_path || "default-banner.jpg"}`}
                  alt={subGroupedAllData[0].subCatalogue_title}
                  className="h-24 w-full object-cover sm:h-56 lg:h-64"
                />
                <div className="absolute inset-0 bg-gradient-to-r from-slate-900/75 via-slate-900/45 to-slate-900/20" />
                <div className="absolute inset-0 flex items-end p-2 sm:p-6">
                  <div className="text-white">
                    <h1 className="font-extrabold sm:text-3xl">
                      {subGroupedAllData[0].subCatalogue_title}
                    </h1>
                  </div>
                </div>
              </section>
            </RevealOnScroll>

            {/* Filters */}
            <RevealOnScroll delayMs={60}>
              <div className="px-4 sm:px-6 mb-5">
                <section className="rounded-lg bg-white px-4 py-2 shadow-sm ring-1 ring-slate-100 sm:px-5 sm:py-5">
                  <div className="grid gap-3 grid-cols-1 sm:grid-cols-4 items-stretch">
                    <div className="relative sm:col-span-2 h-full">
                      <FaSearch className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
                      <input
                        type="text"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        placeholder="Search..."
                        className="w-full h-full rounded-md border border-slate-300 py-2 pl-9 pr-3 text-sm outline-none focus:border-blue-500"
                      />
                    </div>

                    <div className="grid grid-cols-2 gap-2 sm:contents">
                      <div className="flex h-full items-center justify-center gap-1 rounded-md border border-slate-300 bg-white px-2 text-xs sm:text-sm">
                        <FaSortAmountDown className="text-slate-500" />
                        <select
                          value={sortBy}
                          onChange={(e) => setSortBy(e.target.value)}
                          className="bg-transparent outline-none border-none focus:ring-0"
                        >
                          <option value="featured">Sort By</option>
                          <option value="price_low">Low-High</option>
                          <option value="price_high">High-Low</option>
                          <option value="discount_high">Discount</option>
                          <option value="name_az">A-Z</option>
                        </select>
                      </div>

                      <label className="flex h-full items-center justify-center gap-1 rounded-md border border-slate-300 bg-white px-2 text-xs sm:text-sm">
                        <input
                          type="checkbox"
                          checked={freeDeliveryOnly}
                          onChange={(e) =>
                            setFreeDeliveryOnly(e.target.checked)
                          }
                          className="h-4 w-4"
                        />
                        Free Delivery
                      </label>
                    </div>
                  </div>
                </section>
              </div>
            </RevealOnScroll>

            {/* Item Cards */}
            <div className="mt-5">
              {processedItems.length > 0 ? (
                <CardDesign likeData={processedItems} />
              ) : (
                <NoDataFound
                  message={
                    searchTerm
                      ? "No search results found"
                      : freeDeliveryOnly
                        ? "No free delivery items available"
                        : "No items found"
                  }
                />
              )}
            </div>
          </>
        )}
      </div>

      <TrendingProducts />
      <FooterSection />

      {showScrollTop && <ScrollToTopButton />}
    </div>
  );
};

export default SubCategoryWiseData;
